import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import {
  Activity,
  ActivityWithCategoryAndLocationsAndReportedDetails,
  PublicationStatus,
  Shift,
  TimelineShift,
  WilsonApp,
  WithPreparedAttributes,
} from '@wilson/interfaces';
import { createFullActivityObject } from '@wilson/non-domain-specific/activities-helpers/services';
import {
  CheckShiftActivityPermissionsService,
  GanttEndingPixelPipe,
  GanttStartingPixelPipe,
} from '@wilson/shift-timeline/assignments-gantt-chart';
import {
  CreateNewViaDragAndDropService,
  DragAndDropModeService,
  TimelineHelperService,
} from '@wilson/shift-timeline/services';
import {
  SetDemarcatorRange,
  ShiftTimelineState,
  TimelineAssignedShiftValidationsStateService,
  TimelineRemoveShiftFromUserV2,
  TimelineStaysSuggestionService,
  TimelineUpsertActivityV2,
  TimelineUpsertNewShiftV2,
} from '@wilson/shift-timeline/state';
import { ShiftsService } from '@wilson/shifts';
import { DateTimeFormat } from '@wilson/utils';
import { addMilliseconds, differenceInMilliseconds, format } from 'date-fns';
import { NzMessageService } from 'ng-zorro-antd/message';
import { firstValueFrom } from 'rxjs';
import { TimelineAdjustTimeService } from '../../services/timeline-adjust-time.service';
import { TimelineCommonOperationsService } from '../../services/timeline-common-operations.service';
import { TimelineShiftDragAndDropDirectiveHelperService } from '../../services/timeline-shift-drag-and-drop-directive-helper.service';

@Injectable()
export class MoveShiftDropZoneService {
  private readonly store = inject(Store);
  private readonly dragAndDropModeService = inject(DragAndDropModeService);
  private readonly createNewViaDragAndDropService = inject(
    CreateNewViaDragAndDropService,
  );
  private readonly shiftsService = inject(ShiftsService);
  private readonly nzMessageService = inject(NzMessageService);
  private readonly translateService = inject(TranslateService);
  private readonly ganttActivityStartingPixelPipe = inject(
    GanttStartingPixelPipe,
  );
  private readonly ganttActivityEndingPixelPipe = inject(GanttEndingPixelPipe);
  private readonly timelineCommonOperationsService = inject(
    TimelineCommonOperationsService,
  );
  private readonly timelineAdjustTimeService = inject(
    TimelineAdjustTimeService,
  );
  private readonly timelineStaysSuggestionService = inject(
    TimelineStaysSuggestionService,
  );
  private readonly timelineShiftDragAndDropDirectiveHelperService = inject(
    TimelineShiftDragAndDropDirectiveHelperService,
  );
  protected readonly timelineHelperService = inject(TimelineHelperService);
  private readonly checkShiftActivityPermissionsService = inject(
    CheckShiftActivityPermissionsService,
  );
  private readonly timelineAssignedShiftValidationsStateService = inject(
    TimelineAssignedShiftValidationsStateService,
  );

  private readonly ADJUSTED_LEFT_PX = 100;
  public shiftWidth = this.ADJUSTED_LEFT_PX;
  private draggingMockShift!: TimelineShift;

  public async setShiftWidth(): Promise<void> {
    const draggingShift = await firstValueFrom(
      this.dragAndDropModeService.draggingShiftV2$,
    );
    if (draggingShift) {
      const dummyShift = {
        ...draggingShift,
        activities: [this.prepareDummyActivity(draggingShift)],
      };

      const shiftLeftPx = await firstValueFrom(
        this.ganttActivityStartingPixelPipe.transform(
          dummyShift.activities[0].startDatetime,
        ),
      );
      const shiftRightPx = await firstValueFrom(
        this.ganttActivityEndingPixelPipe.transform([
          dummyShift.activities[0].startDatetime,
          dummyShift.activities[dummyShift.activities.length - 1].endDatetime,
        ]),
      );
      const frameLength = this.store.selectSnapshot(
        ShiftTimelineState.frameLength,
      );
      this.shiftWidth = frameLength - shiftRightPx - shiftLeftPx;
    }
  }

  public async onDragOverHandler(
    draggingMockShift: TimelineShift,
  ): Promise<void> {
    this.draggingMockShift = draggingMockShift;

    this.store.dispatch(
      new TimelineUpsertNewShiftV2({
        shift: this.draggingMockShift,
      }),
    );
    this.dragAndDropModeService.setDraggingItemId(this.draggingMockShift.id);
  }

  public async onDragLeaveHandler(): Promise<void> {
    const draggingShift = await firstValueFrom(
      this.dragAndDropModeService.draggingShiftV2$,
    );
    if (draggingShift?.id && draggingShift.userId) {
      this.resetShiftToOldPosition(draggingShift);
    }
  }

  public async onDropHandler(): Promise<void> {
    this.createNewViaDragAndDropService.setQuickShiftDraggable(false);
    const draggingShift = await firstValueFrom(
      this.dragAndDropModeService.draggingShiftV2$,
    );

    const userConfirmedPublishedShiftEdit = draggingShift
      ? await this.timelineCommonOperationsService.checkAndConfirmPublishedShiftDrop(
          draggingShift.publicationStatus,
        )
      : false;

    this.dragAndDropModeService.setDraggingItemId(null);
    if (draggingShift?.userId && draggingShift?.id) {
      if (userConfirmedPublishedShiftEdit) {
        this.createNewViaDragAndDropService.addUpdatingId(draggingShift.id);

        firstValueFrom(
          this.shiftsService.get(draggingShift.id, {
            relations: [
              `organizationalUnit`,
              `user`,
              `shiftSeries`,
              `shiftCategory`,
              `activities`,
              `activities.endLocation`,
              `activities.startLocation`,
              `activities.activityCategory`,
              `activities.activityReports`,
              `activities.activityReports.location`,
              'labels',
            ],
          }),
        )
          .then(async (shift) => {
            const shiftWithActivities = shift as Shift &
              WithPreparedAttributes & {
                id: string;
                activities: Activity[];
                priority: number | null;
              };
            shiftWithActivities.activities.sort((a, b) =>
              differenceInMilliseconds(
                new Date(a.startDatetime),
                new Date(b.startDatetime),
              ),
            );
            const allowedToMoveSharedActivity = await firstValueFrom(
              this.timelineCommonOperationsService.allowedToMoveShift(
                shiftWithActivities,
              ),
            );

            const canUpdateIfServiceActivitiesExist = await firstValueFrom(
              this.checkShiftActivityPermissionsService.canUpdateWhenServiceActivitiesExist(
                shiftWithActivities.activities,
              ),
            );

            if (
              !canUpdateIfServiceActivitiesExist ||
              !allowedToMoveSharedActivity
            ) {
              if (!canUpdateIfServiceActivitiesExist) {
                this.timelineHelperService.informUserHasNoPermissionsToMoveEntity();
              }

              if (!allowedToMoveSharedActivity) {
                this.timelineCommonOperationsService.showSharedActivityUpdateRestrictionModal();
              }
              this.createNewViaDragAndDropService.removeUpdatingId(
                shiftWithActivities.id,
              );
              this.resetShiftToOldPosition(draggingShift);
            } else {
              const timeDifference = differenceInMilliseconds(
                new Date(this.draggingMockShift.startDatetime),
                new Date(draggingShift.startDatetime),
              );
              shiftWithActivities.activities.map((activity) => {
                activity.startDatetime = addMilliseconds(
                  new Date(activity.startDatetime),
                  timeDifference,
                ).toISOString();
                activity.endDatetime = addMilliseconds(
                  new Date(activity.endDatetime),
                  timeDifference,
                ).toISOString();
                return activity;
              });
              shiftWithActivities.startDate = format(
                new Date(shiftWithActivities.activities[0].startDatetime),
                DateTimeFormat.DatabaseDateFormat,
              );
              shiftWithActivities.publicationStatus =
                PublicationStatus.NotPublished;

              if (shiftWithActivities) {
                this.shiftsService.updateShift(shiftWithActivities).subscribe({
                  next: (materializedShift: Shift) => {
                    this.store.dispatch(
                      new TimelineUpsertActivityV2(
                        shiftWithActivities.activities as ActivityWithCategoryAndLocationsAndReportedDetails[],
                        materializedShift.id as string,
                      ),
                    );
                    this.timelineAdjustTimeService.adjustV2TimelineShiftTimeAndUpdateValidations(
                      {
                        ...this.draggingMockShift,
                        publicationStatus: PublicationStatus.NotPublished,
                        id: materializedShift.id as string,
                      },
                    );

                    this.timelineStaysSuggestionService.refreshStaysDataForSpecifiedUsers(
                      [shiftWithActivities.userId as string],
                    );
                    this.createNewViaDragAndDropService.setQuickShiftDraggable(
                      true,
                    );
                    this.timelineCommonOperationsService.updateAssignedShiftsKPIs();
                    this.timelineAssignedShiftValidationsStateService.queueValidationRequest(
                      [shiftWithActivities.id],
                      {
                        forceRefresh: true,
                      },
                    );
                  },
                  error: () => {
                    this.resetDragAndDrop(
                      shiftWithActivities.id,
                      shiftWithActivities.userId,
                    );
                  },
                  complete: () => {
                    this.createNewViaDragAndDropService.removeUpdatingId(
                      shiftWithActivities.id,
                    );
                    this.dragAndDropModeService.setDraggingShiftV2(null);
                    this.store.dispatch(new SetDemarcatorRange([]));
                  },
                });
              }
            }
          })
          .catch(() => {
            this.resetDragAndDrop(draggingShift.id, draggingShift.userId);
          });
      } else {
        this.resetShiftToOldPosition(draggingShift);
      }
    }
    this.dragAndDropModeService.setTimeDifferenceVisible(true);
    this.timelineShiftDragAndDropDirectiveHelperService.handleDragEnd(true);
  }

  private resetDragAndDrop(shiftId: string, userId: string | null): void {
    this.store.dispatch(
      new TimelineRemoveShiftFromUserV2({
        userId: userId as string,
        shiftId: shiftId,
      }),
    );
    this.createNewViaDragAndDropService.removeUpdatingId(shiftId);
    this.createNewViaDragAndDropService.setQuickShiftDraggable(true);
    this.nzMessageService.error(
      this.translateService.instant('general.something_went_wrong'),
    );
  }

  private resetShiftToOldPosition(draggingShift: TimelineShift): void {
    this.timelineAdjustTimeService.adjustV2TimelineShiftTime(draggingShift);
    this.dragAndDropModeService.setDraggingShiftV2(null);
    this.dragAndDropModeService.setDraggingItemId(null);
    this.store.dispatch(new SetDemarcatorRange([]));
  }

  private prepareDummyActivity(draggingShift: TimelineShift): Activity {
    return createFullActivityObject({
      activityCategoryId: '',
      createdFrom: WilsonApp.Portal,
      endDatetime: draggingShift.endDatetime,
      endLocationId: draggingShift.endLocationId,
      jobId: null,
      name: '',
      operationalStatus: draggingShift.operationalStatus,
      serviceId: null,
      shiftId: draggingShift.id,
      startDatetime: draggingShift.startDatetime,
      startLocationId: draggingShift.startLocationId,
      viaLocationIds: [],
    });
  }
}
